import { io } from 'socket.io-client';
import { getToken } from './authService';
import { serviceConstants } from './common/constants';

let socket = null;
let connectionAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;

// Minimal logging - only log errors in production, enable full logging for debugging
const isDevelopment = process.env.NODE_ENV === 'development';
const debugLog = isDevelopment ? console.log.bind(console) : () => {};
const debugError = console.error.bind(console); // Always log errors
const debugWarn = isDevelopment ? console.warn.bind(console) : () => {};

// Get backend URL from environment or use same as API
// Remove /api suffix if present since Socket.io connects to root
const getSocketURL = () => {
    let url = serviceConstants.API_HOST_NAME;
    
    // Remove trailing slash
    url = url.replace(/\/+$/, '');
    
    // Remove /api suffix if present since Socket.io connects to root
    if (url.endsWith('/api')) {
        url = url.slice(0, -4);
    }
    
    // Ensure we have a valid URL
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'http://' + url;
    }
    
    return url;
}

export const connectSocket = () => {
    // CRITICAL: If socket instance already exists, ALWAYS return it - never create a new one
    // Socket.io will reuse the same connection if we reuse the instance
    if (socket) {
        return socket;
    }
    
    // Only create new socket if one doesn't exist at all
    const token = getToken();
    
    if (!token) {
        return null;
    }

    const socketURL = getSocketURL();

    try {
        // Create socket instance - this should only happen once per app lifecycle
        // io() is synchronous - it creates the instance immediately
        socket = io(socketURL, {
            // IMPORTANT: Allow reuse of the same connection
            forceNew: false, // Explicitly set to false to allow connection reuse
            // Auto-connect is enabled by default, no need to call connect()
            auth: {
                token: token
            },
            // Also send token in query string as fallback (for proxy compatibility)
            query: {
                token: token
            },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
            // Add path if using reverse proxy (common setup)
            path: '/socket.io/',
            // DON'T force new connection - reuse existing if available
            // forceNew: true, // REMOVED - causes multiple connections
            // Upgrade transport automatically
            upgrade: true,
            // Timeout for connection
            timeout: 20000
        });

        socket.on('connect', () => {
            connectionAttempts = 0; // Reset on successful connection
        });

        socket.on('connect_error', (error) => {
            connectionAttempts++;
            
            // Always log connection errors (important for debugging)
            if (connectionAttempts === 1) {
                debugError('⚠️ [Socket] Connection error:', error.message || error);
            }
            
            if (connectionAttempts >= MAX_RECONNECT_ATTEMPTS) {
                debugError('❌ [Socket] Connection failed after', MAX_RECONNECT_ATTEMPTS, 'attempts. URL:', socketURL);
                socket?.disconnect();
            }
        });

        socket.on('disconnect', (reason) => {
            // Auto-reconnect if disconnected unexpectedly
            if (reason === 'io server disconnect') {
                socket?.connect();
            }
        });

        socket.on('error', (error) => {
            // Only log non-transport errors (transport errors are handled by connect_error)
            if (error && error.type !== 'TransportError') {
                debugError('❌ [Socket] Socket error:', error);
            }
        });

        return socket;
    } catch (error) {
        debugError('❌ [Socket] Error creating socket connection:', error);
        debugError('❌ [Socket] Socket URL was:', socketURL);
        return null;
    }
};

export const disconnectSocket = () => {
    if (socket) {
        socket.disconnect();
        socket = null;
    }
};

export const getSocket = () => {
    return socket;
};

// Store callbacks for proper cleanup
let notificationCallbacks = [];
let notificationReadCallbacks = [];
let allNotificationsReadCallbacks = [];

export const onNotification = (callback) => {
    if (socket) {
        socket.on('notification', callback);
        notificationCallbacks.push(callback);
    }
};

export const onNotificationRead = (callback) => {
    if (socket) {
        socket.on('notification_read', callback);
        notificationReadCallbacks.push(callback);
    }
};

export const onAllNotificationsRead = (callback) => {
    if (socket) {
        socket.on('notifications_all_read', callback);
        allNotificationsReadCallbacks.push(callback);
    }
};

export const removeNotificationListener = (notificationCallback, notificationReadCallback, allNotificationsReadCallback) => {
    if (!socket) {
        return;
    }
    
    // Remove specific callbacks if provided
    if (notificationCallback) {
        socket.off('notification', notificationCallback);
        notificationCallbacks = notificationCallbacks.filter(cb => cb !== notificationCallback);
    }
    
    if (notificationReadCallback) {
        socket.off('notification_read', notificationReadCallback);
        notificationReadCallbacks = notificationReadCallbacks.filter(cb => cb !== notificationReadCallback);
    }
    
    if (allNotificationsReadCallback) {
        socket.off('notifications_all_read', allNotificationsReadCallback);
        allNotificationsReadCallbacks = allNotificationsReadCallbacks.filter(cb => cb !== allNotificationsReadCallback);
    }
    
    // If no callbacks provided, remove all
    if (!notificationCallback && !notificationReadCallback && !allNotificationsReadCallback) {
        notificationCallbacks.forEach(cb => socket.off('notification', cb));
        notificationReadCallbacks.forEach(cb => socket.off('notification_read', cb));
        allNotificationsReadCallbacks.forEach(cb => socket.off('notifications_all_read', cb));
        
        notificationCallbacks = [];
        notificationReadCallbacks = [];
        allNotificationsReadCallbacks = [];
    }
};

// ── Generic emit/on helpers (used by games and any future modules) ──────
export const emitSocket = (event, payload, ack) => {
    if (!socket) return false;
    if (typeof ack === "function") socket.emit(event, payload, ack);
    else socket.emit(event, payload);
    return true;
};

export const onSocket = (event, callback) => {
    if (!socket) return () => {};
    socket.on(event, callback);
    // Return an unsubscribe function so callers don't have to track handlers manually
    return () => {
        try { socket.off(event, callback); } catch { /* ignore */ }
    };
};

// Helper to check if socket is connected and wait for connection
export const waitForSocketConnection = (callback) => {
    if (!socket) {
        return;
    }
    
    if (socket.connected) {
        // Socket already connected, call immediately
        callback(socket);
    } else {
        // Wait for connection
        const connectHandler = () => {
            callback(socket);
        };
        socket.once('connect', connectHandler);
    }
};

// Ping the server to check connection
export const pingServer = () => {
    if (socket?.connected) {
        socket.emit('ping');
    }
};

